import type { OverlayRef } from '@angular/cdk/overlay'
import { isPlatformBrowser } from '@angular/common'
import { EventEmitter, Inject, PLATFORM_ID } from '@angular/core'

import { filter, fromEvent, Observable, Subject, takeUntil } from 'rxjs'

import type { ZardDialogComponent, ZardDialogOptions } from './dialog.component'

const enum eTriggerAction {
    CANCEL = 'cancel',
    OK = 'ok',
}

export class ZardDialogRef<T = unknown, R = unknown, U = unknown> {
    private destroy$ = new Subject<void>()
    private _afterClosed$ = new Subject<R | undefined>()
    private isClosing = false
    protected result?: R
    componentInstance: T | null = null

    constructor(
        private overlayRef: OverlayRef,
        private config: ZardDialogOptions<T, U>,
        private containerInstance: ZardDialogComponent<T, U>,
        @Inject(PLATFORM_ID) private platformId: object,
        private onDispose?: () => void,
    ) {
        this.containerInstance.cancelTriggered.subscribe(() => this.trigger(eTriggerAction.CANCEL))
        this.containerInstance.okTriggered.subscribe(() => this.trigger(eTriggerAction.OK))

        if ((this.config.zMaskClosable ?? true) && isPlatformBrowser(this.platformId)) {
            this.overlayRef
                .outsidePointerEvents()
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    if (this.isTopmostDialog()) {
                        this.close()
                    }
                })
        }

        if (isPlatformBrowser(this.platformId)) {
            fromEvent<KeyboardEvent>(document, 'keydown')
                .pipe(
                    filter((event) => event.key === 'Escape'),
                    takeUntil(this.destroy$),
                )
                .subscribe((event) => {
                    if (!this.isTopmostDialog()) {
                        return
                    }
                    event.preventDefault()
                    event.stopPropagation()
                    this.close()
                })
        }
    }

    /**
     * When multiple CDK overlays are open (e.g. Contact Book + Add Contact), only the top pane
     * should handle Escape / backdrop — otherwise the parent dialog closes and leaves the UI
     * non-interactive behind a stale overlay.
     */
    private isTopmostDialog(): boolean {
        if (!isPlatformBrowser(this.platformId)) return true

        const el = this.overlayRef?.overlayElement
        if (!el) return true

        const panes = document.querySelectorAll('.cdk-overlay-pane')
        return !panes?.length || panes[panes.length - 1] === el
    }

    afterClosed(): Observable<R | undefined> {
        return this._afterClosed$.asObservable()
    }

    afterOpened(): Observable<void> {
        return new Observable((subscriber) => {
            subscriber.next()
            subscriber.complete()
        })
    }

    close(result?: R) {
        if (this.isClosing) {
            return
        }

        this.isClosing = true
        this.result = result

        if (isPlatformBrowser(this.platformId)) {
            const hostElement = this.containerInstance.getNativeElement()
            hostElement.classList.add('dialog-leave')
        }

        setTimeout(() => {
            this.onDispose?.()
            if (this.overlayRef) {
                if (this.overlayRef.hasAttached()) {
                    this.overlayRef.detachBackdrop()
                }
                this.overlayRef.dispose()
            }

            this._afterClosed$.next(this.result)
            this._afterClosed$.complete()

            if (!this.destroy$.closed) {
                this.destroy$.next()
                this.destroy$.complete()
            }
        }, 150)
    }

    private trigger(action: eTriggerAction) {
        const trigger = { ok: this.config.zOnOk, cancel: this.config.zOnCancel }[action]

        if (trigger instanceof EventEmitter) {
            trigger.emit(this.getContentComponent())
        } else if (typeof trigger === 'function') {
            const result = trigger(this.getContentComponent()) as R
            this.closeWithResult(result)
        } else {
            this.close()
        }
    }

    private getContentComponent(): T {
        return this.componentInstance as T
    }

    /**
     * Only close when the user clicked the backdrop (dimmed overlay), not when they
     * clicked inside another overlay (e.g. a dropdown panel rendered in a sibling overlay).
     */
    private isBackdropClick(event: MouseEvent): boolean {
        const target = event.target as Element | null
        if (!target?.closest) return true
        return target.closest('.cdk-overlay-backdrop') != null
    }

    private closeWithResult(result: R): void {
        if (result !== false) {
            this.close(result)
        }
    }
}
